<template>
  <v-layout
    row
    wrap
  >
    <v-flex xs12>
      <VWidget
        :title="$t('Base Prices')"
      >
        <v-layout
          slot="widget-content"
          row
          wrap
        >
          <v-flex xs6>
            <product-price
              v-model="basePrice"
              :vat="vat"
              :gross-disabled="grossDisabled"
              :net-disabled="netDisabled"
            />
          </v-flex>

          <v-flex xs6>
            <product-price
              v-model="salePrice"
              :vat="vat"
              :gross-disabled="grossDisabled"
              :net-disabled="netDisabled"
              net-label="Sale Price"
              gross-label="Gross Sale Price"
            />
          </v-flex>

          <template v-if="tax_type === 'vat'">
            <v-flex
              xs12
              md12
              class="px-4 pt-0"
            >
              <v-select
                v-if="taxes.length > 0"
                v-model="product.vat"
                :disabled="!vat_enabled"
                :label="$t('VAT')"
                :item-text="vatLabel"
                :items="taxes"
                item-value="id"
                return-object
              />
            </v-flex>
          </template>

        </v-layout>
      </VWidget>
    </v-flex>

    <v-flex xs12>
      <VWidget
        :title="$t('Tier Prices')"
      >
        <div slot="widget-header-action">
          <v-btn
            v-if="product.tiers && product.tiers.length"
            flat
            class="text--secondary text-capitalize"
            @click="sortTiers"
          >
            {{ $t('Sort by quantity') }}
            <v-icon v-if="sorting === 'asc'">keyboard_arrow_down</v-icon>
            <v-icon v-else-if="sorting === 'desc'">keyboard_arrow_up</v-icon>
          </v-btn>

          <v-btn
            flat
            class="text--secondary text-capitalize"
            @click="addTier"
          >
            {{ $t('Add tier') }}
          </v-btn>
        </div>

        <v-layout
          slot="widget-content"
          row
          wrap
        >
          <v-flex
            v-for="(tier, index) in product.tiers"
            :key="index"
            xs12
          >
            <product-single-tier-price
              :value="tier"
              :key="index"
              :vat="vat"
              :gross-disabled="grossDisabled"
              :net-disabled="netDisabled"
              :customer-groups="customerGroups"
              @removed="removeTier"
            />
          </v-flex>
        </v-layout>
      </VWidget>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import MoneyInput from '@/components/form/MoneyInput'
import ProductPrice from '@/modules/Products/components/ProductPrice'
import ProductSingleTierPrice from '@/modules/Products/components/ProductSingle/ProductSingleTierPrice'

export default {
  name: 'ProductSinglePrice',

  components: {
    ProductSingleTierPrice,
    ProductPrice,
    MoneyInput
  },

  props: {
    product: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      default: () => {}
    },
    taxes: {
      type: Array,
      required: true
    }
  },

  data () {
    return {
      vat_enabled: this.settings && this.settings.type && this.settings.type === 'vat',
      vat_in_price: this.settings.vat_in_price || false,
      tax_type: this.settings.type || 'sales',
      customerGroups: [],
      sorting: null
    }
  },

  computed: {
    ...mapGetters(['currency']),

    netDisabled () {
      return this.vat_enabled && this.vat_in_price
    },

    grossDisabled () {
      return (this.vat_enabled && !this.vat_in_price) || !this.vat_enabled
    },

    vat () {
      return this.tax_type === 'vat' ? this.product.vat : null
    },

    basePrice: {
      get () {
        return {
          price: this.product.price,
          gross_price: this.product.gross_price || null
        }
      },
      set (value) {
        const price = this.product.price || { amount: 0 }
        if (value.price && price.amount !== value.price.amount) {
          this.product.price = value.price || null
        }

        const grossPrice = this.product.gross_price || { amount: 0 }
        if (value.gross_price && grossPrice.amount !== value.gross_price.amount) {
          this.product.gross_price = value.gross_price || null
        }
      }
    },

    salePrice: {
      get () {
        return {
          price: this.product.sale_price,
          gross_price: this.product.gross_sale_price
        }
      },
      set (value) {
        const salePrice = this.product.sale_price || { amount: 0 }
        if (value.price && salePrice.amount !== value.price.amount) {
          this.product.sale_price = value.price && value.price.amount ? value.price : null
        }

        const grossSalePrice = this.product.gross_sale_price || { amount: 0 }
        if (value.gross_price && grossSalePrice.amount !== value.gross_price.amount) {
          this.product.gross_sale_price = value.gross_price && value.gross_price.amount ? value.gross_price : null
        }
      }
    }
  },

  async mounted () {
    this.customerGroups = await this.$http.make('customer:group').get()
  },

  methods: {
    vatLabel (vat) {
      return `${vat.name} (${vat.value}%)`
    },

    sortTiers () {
      if (!this.sorting) {
        this.sorting = 'asc'
      } else {
        this.sorting = this.sorting === 'asc' ? 'desc' : 'asc'
      }

      const asc = (a, b) => a.quantity - b.quantity
      const desc = (a, b) => b.quantity - a.quantity

      this.product.tiers = (this.product.tiers || []).sort(this.sorting === 'asc' ? asc : desc)
    },

    addTier () {
      if (!this.product.tiers) {
        this.product.tiers = []
      }

      this.product.tiers.unshift({
        price: { amount: 0, currency: null },
        gross_price: { amount: 0, currency: null },
        quantity: 1,
        customer_groups: []
      })
    },

    removeTier (tier) {
      this.product.tiers = this.product.tiers.filter(t => t !== tier)
    }
  }
}
</script>
